import { useEffect, useRef, lazy, Suspense } from "react";
import { Switch, Route, useLocation } from "wouter";
import React from "react";
import { queryClient, setAttributionCtxProvider } from "./lib/queryClient";
import { authService } from "@/services/AuthService";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { UserProfileProvider } from "@/hooks/useUserProfile";
import { useReferralCapture } from "@/hooks/useReferralCapture";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { useAttribution } from "@/hooks/useAttribution";
import RequirePermission from "@/components/RequirePermission";
import FeedbackWidget from "@/components/FeedbackWidget";
import { RoleType } from "@shared/permissions";

// Home and NotFound stay eager: Home is the initial route (loaded on every
// visit), NotFound is the fallback that must render synchronously if no other
// route matches. Everything else is code-split.
import Home from "@/pages/Home";
import NotFound from "@/pages/not-found";

const Rewards = lazy(() => import("@/pages/Rewards"));
const Redeem = lazy(() => import("@/pages/Redeem"));
const RedemptionHistory = lazy(() => import("@/pages/RedemptionHistory"));
const Leaderboard = lazy(() => import("@/pages/Leaderboard"));
const Referrals = lazy(() => import("@/pages/Referrals"));
const Restaurant = lazy(() => import("@/pages/Restaurant"));
const RestaurantList = lazy(() => import("@/pages/RestaurantList"));
const RestaurantMapView = lazy(() => import("@/pages/RestaurantMapView"));
const Checkout = lazy(() => import("@/pages/Checkout"));
const OrderSuccess = lazy(() => import("@/pages/OrderSuccess"));
const FriendProfile = lazy(() => import("@/pages/FriendProfile"));
const Profile = lazy(() => import("@/pages/Profile"));
const SettingsPersonal = lazy(() => import("@/pages/SettingsPersonal"));
const SettingsPreferences = lazy(() => import("@/pages/SettingsPreferences"));
const MyReviews = lazy(() => import("@/pages/MyReviews"));
const ReviewDetail = lazy(() => import("@/pages/ReviewDetail"));
const FriendsActivity = lazy(() => import("@/components/FriendsActivity"));
const Login = lazy(() => import("@/pages/Login"));
const Signup = lazy(() => import("@/pages/Signup"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const AuthRedirect = lazy(() => import("@/pages/AuthRedirect"));
const TestAuth = lazy(() => import("@/pages/TestAuth"));
const TestCheckoutFlow = lazy(() => import("@/pages/TestCheckoutFlow"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const DataDeletion = lazy(() => import("@/pages/DataDeletion"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminReviews = lazy(() => import("@/pages/admin/AdminReviews"));
const AdminReports = lazy(() => import("@/pages/admin/AdminReports"));
const AdminComments = lazy(() => import("@/pages/admin/AdminComments"));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminTags = lazy(() => import("@/pages/admin/AdminTags"));
const AdminGamification = lazy(() => import("@/pages/admin/AdminGamification"));
const AdminSettings = lazy(() => import("@/pages/admin/AdminSettings"));
const AdminRestaurants = lazy(() => import("@/pages/admin/AdminRestaurants"));
const AdminRestaurantManage = lazy(
  () => import("@/pages/admin/AdminRestaurantManage")
);
const AdminCoupons = lazy(() => import("@/pages/admin/AdminCoupons"));
const AdminEatzyAccess = lazy(
  () => import("@/pages/admin/AdminEatzyAccess")
);
const AdminStripeConnect = lazy(
  () => import("@/pages/admin/AdminStripeConnect")
);
const AdminConfig = lazy(() => import("@/pages/admin/AdminConfig"));
const AdminMobileAppVersion = lazy(
  () => import("@/pages/admin/AdminMobileAppVersion")
);
const AdminFeedback = lazy(() => import("@/pages/admin/AdminFeedback"));
const AdminTrustedDomains = lazy(
  () => import("@/pages/admin/AdminTrustedDomains")
);
const AdminQuickPostAiMetrics = lazy(
  () => import("@/pages/admin/AdminQuickPostAiMetrics")
);
const AdminSmartFixEvaluations = lazy(
  () => import("@/pages/admin/AdminSmartFixEvaluations")
);
const AdminKudozProbe = lazy(() => import("@/pages/admin/AdminKudozProbe"));
const AdminKudozRuleEffects = lazy(
  () => import("@/pages/admin/AdminKudozRuleEffects")
);
const AdminErrorLog = lazy(() => import("@/pages/admin/AdminErrorLog"));
const AdminDiagnostics = lazy(() => import("@/pages/admin/AdminDiagnostics"));
const AdminSocialSupport = lazy(
  () => import("@/pages/admin/AdminSocialSupport")
);
const AdminHousePostsEnrollments = lazy(
  () => import("@/pages/admin/AdminHousePostsEnrollments")
);
const AdminReferralNetwork = lazy(
  () => import("@/pages/admin/AdminReferralNetwork")
);
const AdminPayoutRuns = lazy(() => import("@/pages/admin/AdminPayoutRuns"));
const AdminAudioLibrary = lazy(
  () => import("@/pages/admin/AdminAudioLibrary")
);
const AdminRestaurantDuplicates = lazy(
  () => import("@/pages/admin/AdminRestaurantDuplicates")
);
const MenuLinkErrorHarness = lazy(
  () => import("@/pages/__test/MenuLinkErrorHarness")
);
const MenuEditorBannerHarness = lazy(
  () => import("@/pages/__test/MenuEditorBannerHarness")
);
const ReviewCardSnapshotHarness = lazy(
  () => import("@/pages/__test/ReviewCardSnapshotHarness")
);
const FeedPerformanceHarness = lazy(
  () => import("@/pages/__test/FeedPerformanceHarness")
);
const RestaurantPostCardSnapshotHarness = lazy(
  () => import("@/pages/__test/RestaurantPostCardSnapshotHarness")
);
const LiveSpineDiffViewHarness = lazy(
  () => import("@/pages/__test/LiveSpineDiffViewHarness")
);
const RemovedDiffKeepEditHarness = lazy(
  () => import("@/pages/__test/RemovedDiffKeepEditHarness")
);
const PartnerLogin = lazy(() => import("@/pages/partner/PartnerLogin"));
const PartnerDashboard = lazy(() => import("@/pages/partner/PartnerDashboard"));
const PartnerReviews = lazy(() => import("@/pages/partner/PartnerReviews"));
const PartnerPosts = lazy(() => import("@/pages/partner/PartnerPosts"));
const PartnerKudoz = lazy(() => import("@/pages/partner/PartnerKudoz"));
const KudozDebug = lazy(() => import("@/pages/KudozDebug"));
const DebugLogs = lazy(() => import("@/pages/DebugLogs"));
const PartnerPromotions = lazy(
  () => import("@/pages/partner/PartnerPromotions")
);
const PartnerAnalytics = lazy(() => import("@/pages/partner/PartnerAnalytics"));
const PartnerInfluencers = lazy(
  () => import("@/pages/partner/PartnerInfluencers")
);
const PartnerSettings = lazy(() => import("@/pages/partner/PartnerSettings"));
const PartnerHousePosts = lazy(() => import("@/pages/partner/PartnerHousePosts"));
const PartnerSocialConnect = lazy(
  () => import("@/pages/partner/PartnerSocialConnect")
);
const PartnerFacebookPageSelect = lazy(
  () => import("@/pages/partner/PartnerFacebookPageSelect")
);
const PartnerMenuEditor = lazy(
  () => import("@/pages/partner/PartnerMenuEditor")
);
const PartnerCoupons = lazy(() => import("@/pages/partner/PartnerCoupons"));
const PartnerCampaigns = lazy(() => import("@/pages/partner/PartnerCampaigns"));
const PartnerKudozIncentives = lazy(
  () => import("@/pages/partner/PartnerKudozIncentives")
);
const PartnerWebsite = lazy(() => import("@/pages/partner/PartnerWebsite"));
const PartnerPost = lazy(() => import("@/pages/partner/PartnerPost"));
const PartnerLoyalty = lazy(() => import("@/pages/partner/PartnerLoyalty"));
const PartnerTools = lazy(() => import("@/pages/partner/PartnerTools"));
const PartnerQuickPostPermissions = lazy(
  () => import("@/pages/partner/PartnerQuickPostPermissions")
);
const PartnerMobileStatus = lazy(
  () => import("@/pages/partner/PartnerMobileStatus")
);
const AcceptInvite = lazy(() => import("@/pages/AcceptInvite"));
const PartnerPerformance = lazy(
  () => import("@/pages/partner/PartnerPerformance")
);
const PartnerQuickPostPerformance = lazy(
  () => import("@/pages/partner/QuickPostPerformance")
);
const PartnerContentSupply = lazy(() => import("@/pages/partner/ContentSupply"));
const AdminContentSupply = lazy(() => import("@/pages/partner/ContentSupply").then((m) => ({ default: m.AdminContentSupply })));
const AdminQuickPostPerformance = lazy(
  () => import("@/pages/partner/QuickPostPerformance").then((m) => ({ default: m.AdminQuickPostPerformance }))
);
const PartnerCommerceAttribution = lazy(() => import("@/pages/partner/CommerceAttribution"));
const AdminCommerceAttribution = lazy(() => import("@/pages/partner/CommerceAttribution").then((m) => ({ default: m.AdminCommerceAttribution })));
const PartnerCreatorCommerce = lazy(() => import("@/pages/partner/CreatorCommerce").then((m) => ({ default: m.PartnerCreatorCommerce })));
const AdminCreatorCommerce = lazy(() => import("@/pages/partner/CreatorCommerce").then((m) => ({ default: m.AdminCreatorCommerce })));
const PartnerInbox = lazy(() => import("@/pages/partner/PartnerInbox"));
const PartnerSchedule = lazy(() => import("@/pages/partner/PartnerSchedule"));
const GiftCertificates = lazy(() => import("@/pages/GiftCertificates"));
const PurchaseGiftCertificate = lazy(
  () => import("@/pages/PurchaseGiftCertificate")
);
const OwnerLanding = lazy(() => import("@/pages/owner/OwnerLanding"));
const OwnerOnboarding = lazy(() => import("@/pages/owner/OwnerOnboarding"));
const OwnerSuccess = lazy(() => import("@/pages/owner/OwnerSuccess"));
const About = lazy(() => import("@/pages/About"));
const ContactUs = lazy(() => import("@/pages/ContactUs"));
const Support = lazy(() => import("@/pages/Support"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const CompetitiveReport = lazy(() => import("@/pages/CompetitiveReport"));
const PublicRestaurantMenu = lazy(() => import("@/pages/PublicRestaurantMenu"));
const MyEatzy = lazy(() => import("@/pages/MyEatzy"));
const Deals = lazy(() => import("@/pages/Deals"));
const HelpAccountDeletion = lazy(() => import("@/pages/HelpAccountDeletion"));

import { Loader2 } from "lucide-react";
import ConsumerLayout from "@/components/ConsumerLayout";

// Fallback shown while a lazy-loaded route chunk is downloading.
function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin" />
    </div>
  );
}

function InitialPathHandler() {
  const { isLoading } = useAuth();
  const [, setLocation] = useLocation();
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current || isLoading) return;
    handled.current = true;

    let targetPath: string | null = null;

    const params = new URLSearchParams(window.location.search);
    const queryPath = params.get("path");
    if (queryPath) {
      targetPath = queryPath;
    }

    if (!targetPath && window.location.hash.startsWith("#/")) {
      targetPath = window.location.hash.slice(1);
    }

    if (
      targetPath &&
      targetPath.startsWith("/") &&
      targetPath !== "/" &&
      targetPath !== window.location.pathname &&
      !/[^a-zA-Z0-9/_\-.]/.test(targetPath)
    ) {
      const url = new URL(window.location.href);
      url.searchParams.delete("path");
      url.hash = "";
      window.history.replaceState(null, "", url.pathname + url.search);
      setLocation(targetPath);
    }
  }, [isLoading, setLocation]);

  return null;
}

// Protected Route Component
function ProtectedRoute({
  component: Component,
}: {
  component: React.ComponentType;
}) {
  const { isAuthenticated, isLoading } = useAuth();
  const [location, setLocation] = useLocation();

  React.useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      const returnUrl = encodeURIComponent(location);
      setLocation(`/login?returnUrl=${returnUrl}`);
    }
  }, [isAuthenticated, isLoading, location, setLocation]);

  if (isLoading || !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return <Component />;
}

// Consumer Protected Route — wraps the page in the ConsumerLayout (sidebar + bottom nav)
function ConsumerProtectedRoute({
  component: Component,
}: {
  component: React.ComponentType;
}) {
  const { isAuthenticated, isLoading } = useAuth();
  const [location, setLocation] = useLocation();

  React.useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      const returnUrl = encodeURIComponent(location);
      setLocation(`/login?returnUrl=${returnUrl}`);
    }
  }, [isAuthenticated, isLoading, location, setLocation]);

  if (isLoading || !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <ConsumerLayout>
      <Component />
    </ConsumerLayout>
  );
}

// Browsable Route — accessible by anyone (authenticated or not), wrapped in ConsumerLayout
function BrowsableRoute({
  component: Component,
}: {
  component: React.ComponentType;
}) {
  const { isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <ConsumerLayout>
      <Component />
    </ConsumerLayout>
  );
}

// Public Route Component (redirects to home if already authenticated)
function PublicRoute({
  component: Component,
}: {
  component: React.ComponentType;
}) {
  const { isAuthenticated, isLoading } = useAuth();
  const [, setLocation] = useLocation();
  // Track whether we have rendered the child component at least once.
  // The spinner should only block on the very first auth check (app startup).
  // Once Login/Signup has been shown, never unmount it mid-flight — login/signup
  // operations set isLoading=true briefly, which would otherwise destroy the
  // component and discard its error state before setError can run.
  const hasRenderedRef = React.useRef(false);

  React.useEffect(() => {
    if (isAuthenticated && !isLoading) {
      const params = new URLSearchParams(window.location.search);
      const returnUrl = params.get("returnUrl");
      const destination =
        returnUrl && returnUrl !== "/" ? returnUrl : "/auth/redirect";
      setLocation(destination);
    }
  }, [isAuthenticated, isLoading, setLocation]);

  // Show spinner only during the initial app-load auth check, before we know
  // whether the user is authenticated (prevents a flash of the login form).
  if (!hasRenderedRef.current && isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // Redirect pending — keep showing spinner while useEffect fires the navigation.
  if (isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  hasRenderedRef.current = true;
  return <Component />;
}

// Wires referral click-tracking for every page a referral link can land on.
// Must live inside Router so wouter's useSearch() has the correct context.
function ReferralInit() {
  useReferralCapture();
  return null;
}

function Router() {
  return (
    <>
      <ReferralInit />
      <Suspense fallback={<RouteFallback />}>
        <Switch>
          {/* Public routes */}
          <Route
            path="/login"
            component={() => <PublicRoute component={Login} />}
          />
          <Route
            path="/signup"
            component={() => <PublicRoute component={Signup} />}
          />
          <Route
            path="/forgot-password"
            component={() => <PublicRoute component={ForgotPassword} />}
          />
          <Route path="/reset-password" component={ResetPassword} />
          <Route path="/auth/callback" component={AuthCallback} />
          <Route path="/auth/redirect" component={AuthRedirect} />
          <Route path="/test-auth" component={TestAuth} />
          <Route path="/test-checkout" component={TestCheckoutFlow} />
          {import.meta.env.DEV && (
            <>
              <Route
                path="/__test/menu-link-error"
                component={MenuLinkErrorHarness}
              />
              <Route
                path="/__test/menu-editor-banner"
                component={MenuEditorBannerHarness}
              />
            </>
          )}
          {import.meta.env.DEV && (
            <Route
              path="/__test/feed-performance"
              component={FeedPerformanceHarness}
            />
          )}
          {import.meta.env.DEV && (
            <Route
              path="/__test/review-card"
              component={ReviewCardSnapshotHarness}
            />
          )}
          {import.meta.env.DEV && (
            <Route
              path="/__test/restaurant-post-card"
              component={RestaurantPostCardSnapshotHarness}
            />
          )}
          {import.meta.env.DEV && (
            <Route
              path="/__test/live-spine-diff"
              component={LiveSpineDiffViewHarness}
            />
          )}
          {import.meta.env.DEV && (
            <Route
              path="/__test/removed-diff-keep-edit"
              component={RemovedDiffKeepEditHarness}
            />
          )}

          {/* Consumer routes — wrapped in ConsumerLayout (desktop sidebar + mobile bottom nav) */}
          <Route
            path="/"
            component={() => <BrowsableRoute component={Home} />}
          />
          <Route
            path="/friends"
            component={() => (
              <ConsumerProtectedRoute component={FriendsActivity} />
            )}
          />
          <Route
            path="/my-eatzy"
            component={() => <ConsumerProtectedRoute component={MyEatzy} />}
          />
          <Route
            path="/deals"
            component={() => <BrowsableRoute component={Deals} />}
          />
          <Route
            path="/rewards"
            component={() => <ConsumerProtectedRoute component={Rewards} />}
          />
          <Route
            path="/redeem"
            component={() => <ConsumerProtectedRoute component={Redeem} />}
          />
          <Route
            path="/redeem/history"
            component={() => (
              <ConsumerProtectedRoute component={RedemptionHistory} />
            )}
          />
          <Route
            path="/leaderboard"
            component={() => <ConsumerProtectedRoute component={Leaderboard} />}
          />
          <Route
            path="/referrals"
            component={() => <ConsumerProtectedRoute component={Referrals} />}
          />
          <Route
            path="/restaurant/:id"
            component={() => <BrowsableRoute component={Restaurant} />}
          />
          <Route
            path="/restaurants"
            component={() => <BrowsableRoute component={RestaurantList} />}
          />
          <Route
            path="/restaurants/map"
            component={() => <BrowsableRoute component={RestaurantMapView} />}
          />
          <Route
            path="/checkout"
            component={() => <ProtectedRoute component={Checkout} />}
          />
          <Route
            path="/order-success/:checkoutId/:clientId"
            component={() => <ProtectedRoute component={OrderSuccess} />}
          />
          <Route
            path="/profile/settings/personal"
            component={() => (
              <ConsumerProtectedRoute component={SettingsPersonal} />
            )}
          />
          <Route
            path="/profile/settings/preferences"
            component={() => (
              <ConsumerProtectedRoute component={SettingsPreferences} />
            )}
          />
          <Route
            path="/profile/gift-certificates"
            component={() => (
              <ConsumerProtectedRoute component={GiftCertificates} />
            )}
          />
          <Route
            path="/purchase-gift-certificate/:clientId"
            component={() => (
              <ProtectedRoute component={PurchaseGiftCertificate} />
            )}
          />
          <Route
            path="/profile/reviews"
            component={() => <ConsumerProtectedRoute component={MyReviews} />}
          />
          {/* Public review detail page — matches the SSR `/review/:id` Open Graph
            handler so the link card people open lands on the actual review. */}
          <Route
            path="/review/:id"
            component={() => <BrowsableRoute component={ReviewDetail} />}
          />
          <Route
            path="/profile/:userId"
            component={() => (
              <ConsumerProtectedRoute component={FriendProfile} />
            )}
          />
          <Route
            path="/profile"
            component={() => <ConsumerProtectedRoute component={Profile} />}
          />
          <Route
            path="/admin"
            component={() => <ProtectedRoute component={AdminDashboard} />}
          />
          <Route
            path="/admin/restaurants"
            component={() => <ProtectedRoute component={AdminRestaurants} />}
          />
          <Route
            path="/admin/restaurants/:eatzyId/manage"
            component={() => (
              <ProtectedRoute component={AdminRestaurantManage} />
            )}
          />
          <Route
            path="/admin/reviews"
            component={() => <ProtectedRoute component={AdminReviews} />}
          />
          <Route
            path="/admin/reports"
            component={() => <ProtectedRoute component={AdminReports} />}
          />
          <Route
            path="/admin/comments"
            component={() => <ProtectedRoute component={AdminComments} />}
          />
          <Route
            path="/admin/users"
            component={() => <ProtectedRoute component={AdminUsers} />}
          />
          <Route
            path="/admin/tags"
            component={() => <ProtectedRoute component={AdminTags} />}
          />
          <Route
            path="/admin/eatzy-access"
            component={() => <ProtectedRoute component={AdminEatzyAccess} />}
          />
          <Route
            path="/admin/gamification"
            component={() => <ProtectedRoute component={AdminGamification} />}
          />
          <Route
            path="/admin/settings"
            component={() => <ProtectedRoute component={AdminSettings} />}
          />
          <Route
            path="/admin/coupons"
            component={() => <ProtectedRoute component={AdminCoupons} />}
          />
          <Route
            path="/admin/stripe-connect"
            component={() => <ProtectedRoute component={AdminStripeConnect} />}
          />
          <Route
            path="/admin/config"
            component={() => <ProtectedRoute component={AdminConfig} />}
          />
          <Route
            path="/admin/mobile-app-version"
            component={() => (
              <ProtectedRoute component={AdminMobileAppVersion} />
            )}
          />
          <Route
            path="/admin/feedback"
            component={() => <ProtectedRoute component={AdminFeedback} />}
          />
          <Route
            path="/admin/trusted-domains"
            component={() => <ProtectedRoute component={AdminTrustedDomains} />}
          />
          <Route
            path="/admin/kudoz-probe"
            component={() => <ProtectedRoute component={AdminKudozProbe} />}
          />
          <Route
            path="/admin/kudoz-rule-effects"
            component={() => (
              <ProtectedRoute component={AdminKudozRuleEffects} />
            )}
          />
          <Route
            path="/admin/error-log"
            component={() => <ProtectedRoute component={AdminErrorLog} />}
          />
          <Route
            path="/admin/diagnostics"
            component={() => <ProtectedRoute component={AdminDiagnostics} />}
          />
          <Route
            path="/admin/social-support"
            component={() => <ProtectedRoute component={AdminSocialSupport} />}
          />
          <Route
            path="/admin/house-posts-enrollments"
            component={() => <ProtectedRoute component={AdminHousePostsEnrollments} />}
          />
          <Route
            path="/admin/referral-network"
            component={() => (
              <ProtectedRoute component={AdminReferralNetwork} />
            )}
          />
          <Route
            path="/admin/quickpost-ai-metrics"
            component={() => (
              <ProtectedRoute component={AdminQuickPostAiMetrics} />
            )}
          />
          <Route
            path="/admin/smartfix-evaluations"
            component={() => (
              <ProtectedRoute component={AdminSmartFixEvaluations} />
            )}
          />
          <Route path="/admin/quickpost-performance" component={() => <ProtectedRoute component={AdminQuickPostPerformance} />} />
          <Route path="/admin/commerce-attribution" component={() => <ProtectedRoute component={AdminCommerceAttribution} />} />
           <Route path="/admin/creator-commerce" component={() => <ProtectedRoute component={AdminCreatorCommerce} />} />
          <Route path="/admin/content-supply" component={() => <ProtectedRoute component={AdminContentSupply} />} />
          <Route
            path="/admin/payout-runs"
            component={() => <ProtectedRoute component={AdminPayoutRuns} />}
          />
          <Route
            path="/admin/audio-library"
            component={() => <ProtectedRoute component={AdminAudioLibrary} />}
          />
          <Route
            path="/admin/restaurants/duplicates"
            component={() => (
              <ProtectedRoute component={AdminRestaurantDuplicates} />
            )}
          />
          <Route path="/partner/login" component={PartnerLogin} />
          <Route
            path="/partner/social/connect"
            component={PartnerSocialConnect}
          />
          <Route
            path="/partner/facebook-page-select"
            component={PartnerFacebookPageSelect}
          />
          <Route
            path="/partner"
            component={() => (
              <RequirePermission
                component={PartnerDashboard}
                role={RoleType.RestaurantManager}
                allowPartnerMembership
              />
            )}
          />
          <Route
            path="/partner/menu"
            component={() => (
              <RequirePermission
                component={PartnerMenuEditor}
                role={RoleType.RestaurantManager}
                allowPartnerMembership
              />
            )}
          />
          <Route
            path="/partner/reviews"
            component={() => (
              <RequirePermission
                component={PartnerReviews}
                role={RoleType.RestaurantManager}
                allowPartnerMembership
              />
            )}
          />
          <Route
            path="/partner/posts"
            component={() => (
              <RequirePermission
                component={PartnerPosts}
                role={RoleType.RestaurantManager}
                allowPartnerMembership
              />
            )}
          />
          <Route
            path="/partner/kudoz"
            component={() => (
              <RequirePermission
                component={PartnerKudoz}
                role={RoleType.RestaurantManager}
                allowPartnerMembership
              />
            )}
          />
          <Route
            path="/partner/coupons"
            component={() => (
              <RequirePermission
                component={PartnerCoupons}
                role={RoleType.RestaurantManager}
                allowPartnerMembership
              />
            )}
          />
          <Route
            path="/partner/campaigns"
            component={() => (
              <RequirePermission
                component={PartnerCampaigns}
                role={RoleType.RestaurantManager}
                allowPartnerMembership
              />
            )}
          />
          <Route
            path="/partner/kudoz-incentives"
            component={() => (
              <RequirePermission
                component={PartnerKudozIncentives}
                role={RoleType.RestaurantManager}
                allowPartnerMembership
              />
            )}
          />
          <Route
            path="/partner/promotions"
            component={() => (
              <RequirePermission
                component={PartnerPromotions}
                role={RoleType.RestaurantManager}
                allowPartnerMembership
              />
            )}
          />
          <Route
            path="/partner/analytics"
            component={() => (
              <RequirePermission
                component={PartnerAnalytics}
                role={RoleType.RestaurantManager}
                allowPartnerMembership
              />
            )}
          />
          <Route
            path="/partner/creator-commerce"
            component={() => (
              <RequirePermission
                component={PartnerCreatorCommerce}
                role={RoleType.RestaurantManager}
                allowPartnerMembership
              />
            )}
          />
          <Route
            path="/partner/influencers"
            component={() => (
              <RequirePermission
                component={PartnerInfluencers}
                role={RoleType.RestaurantManager}
                allowPartnerMembership
              />
            )}
          />
          <Route
            path="/partner/settings"
            component={() => (
              <RequirePermission
                component={PartnerSettings}
                role={RoleType.RestaurantManager}
                allowPartnerMembership
              />
            )}
          />
          <Route
            path="/partner/house-posts/setup"
            component={() => (
              <RequirePermission component={PartnerHousePosts} role={RoleType.RestaurantManager} allowPartnerMembership />
            )}
          />
          <Route
            path="/partner/house-posts/:id"
            component={() => (
              <RequirePermission component={PartnerHousePosts} role={RoleType.RestaurantManager} allowPartnerMembership />
            )}
          />
          <Route
            path="/partner/house-posts"
            component={() => (
              <RequirePermission component={PartnerHousePosts} role={RoleType.RestaurantManager} allowPartnerMembership />
            )}
          />
          <Route
            path="/partner/website"
            component={() => (
              <RequirePermission
                component={PartnerWebsite}
                role={RoleType.RestaurantManager}
                allowPartnerMembership
              />
            )}
          />
          <Route
            path="/partner/post"
            component={() => (
              <RequirePermission
                component={PartnerPost}
                role={RoleType.RestaurantManager}
                allowPartnerMembership
              />
            )}
          />
          <Route
            path="/partner/loyalty"
            component={() => (
              <RequirePermission
                component={PartnerLoyalty}
                role={RoleType.RestaurantManager}
                allowPartnerMembership
              />
            )}
          />
          <Route
            path="/partner/performance"
            component={() => (
              <RequirePermission
                component={PartnerPerformance}
                role={RoleType.RestaurantManager}
                allowPartnerMembership
              />
            )}
          />
          <Route path="/partner/quickpost-performance" component={() => <RequirePermission component={PartnerQuickPostPerformance} role={RoleType.RestaurantManager} allowPartnerMembership />} />
          <Route path="/partner/commerce-attribution" component={() => <RequirePermission component={PartnerCommerceAttribution} role={RoleType.RestaurantManager} allowPartnerMembership />} />
          <Route path="/partner/content-supply" component={() => <RequirePermission component={PartnerContentSupply} role={RoleType.RestaurantManager} allowPartnerMembership />} />
          <Route
            path="/partner/tools"
            component={() => (
              <RequirePermission
                component={PartnerTools}
                role={RoleType.RestaurantManager}
                allowPartnerMembership
              />
            )}
          />
          <Route
            path="/partner/quickpost-permissions"
            component={() => (
              <RequirePermission
                component={PartnerQuickPostPermissions}
                role={RoleType.RestaurantManager}
                allowPartnerMembership
              />
            )}
          />
          <Route
            path="/partner/mobile/status"
            component={() => (
              <RequirePermission
                component={PartnerMobileStatus}
                role={RoleType.RestaurantManager}
                allowPartnerMembership
              />
            )}
          />
          <Route path="/invite/:token" component={AcceptInvite} />
          <Route
            path="/partner/inbox"
            component={() => (
              <RequirePermission
                component={PartnerInbox}
                role={RoleType.RestaurantManager}
                allowPartnerMembership
              />
            )}
          />
          <Route
            path="/partner/schedule"
            component={() => (
              <RequirePermission
                component={PartnerSchedule}
                role={RoleType.RestaurantManager}
                allowPartnerMembership
              />
            )}
          />
          <Route
            path="/debug/logs"
            component={() => <ProtectedRoute component={DebugLogs} />}
          />
          <Route
            path="/kudoz/debug"
            component={() => <ProtectedRoute component={KudozDebug} />}
          />

          {/* Public restaurant menu page (deep link from social posts) */}
          <Route path="/r/:eatzyId" component={PublicRestaurantMenu} />

          {/* Owner portal (public landing, onboarding with step 3 auth gate) */}
          <Route path="/for-restaurants" component={OwnerLanding} />
          <Route path="/owner" component={OwnerLanding} />
          <Route path="/owner/onboarding" component={OwnerOnboarding} />
          <Route path="/owner/success" component={OwnerSuccess} />

          {/* Top-level Gift Certificates route (accessible from nav menu) */}
          <Route
            path="/gift-certificates"
            component={() => <ProtectedRoute component={GiftCertificates} />}
          />

          <Route path="/about" component={About} />
          <Route path="/contact" component={ContactUs} />
          <Route path="/support" component={Support} />
          <Route
            path="/faq"
            component={() => <ProtectedRoute component={FAQ} />}
          />
          <Route path="/privacy" component={PrivacyPolicy} />
          <Route path="/terms" component={TermsOfService} />
          <Route path="/data-deletion" component={DataDeletion} />
          <Route
            path="/help/account-deletion"
            component={HelpAccountDeletion}
          />
          <Route path="/report/competitive" component={CompetitiveReport} />
          <Route component={NotFound} />
        </Switch>
      </Suspense>
    </>
  );
}

function AttributionInit() {
  const { getAttributionCtx } = useAttribution();
  const registered = useRef(false);

  useEffect(() => {
    if (registered.current) return;
    registered.current = true;
    setAttributionCtxProvider(getAttributionCtx);
  }, [getAttributionCtx]);

  return null;
}

function EatzyTokenRefresher() {
  useEffect(() => {
    const handler = async () => {
      const refreshed = await authService.refreshEatzyToken();
      if (refreshed) {
        queryClient.invalidateQueries();
      }
    };
    window.addEventListener("eatzy:token-expired", handler);
    return () => window.removeEventListener("eatzy:token-expired", handler);
  }, []);

  return null;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <UserProfileProvider>
          <TooltipProvider>
            <AttributionInit />
            <EatzyTokenRefresher />
            <Toaster />
            <InitialPathHandler />
            <Router />
            <FeedbackWidget />
          </TooltipProvider>
        </UserProfileProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
